// Tiny pub/sub used to decouple the axios interceptor from React state.
// When the API returns 401 (token missing/expired/invalid), the interceptor
// emits "auth:session-expired" and AuthProvider listens, clears state, and
// performs the redirect to the correct login page.

export type SessionExpiredReason =
  | 'TOKEN_EXPIRED'
  | 'TOKEN_INVALID'
  | 'TOKEN_MISSING'
  | 'USER_INACTIVE'
  | 'AUTH_FAILED'
  | 'UNKNOWN';

export interface SessionExpiredDetail {
  reason: SessionExpiredReason;
  message: string;
}

export const SESSION_EXPIRED_EVENT = 'auth:session-expired';

let lastEmittedAt = 0;

export function emitSessionExpired(detail: SessionExpiredDetail): void {
  // Debounce: a single expired token can produce a burst of 401s as multiple
  // queries fail simultaneously. Suppress duplicate emits within 1.5s so we
  // only fire one logout/redirect.
  const now = Date.now();
  if (now - lastEmittedAt < 1500) return;
  lastEmittedAt = now;

  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<SessionExpiredDetail>(SESSION_EXPIRED_EVENT, { detail }));
}

// Decode a JWT payload without verifying the signature. Used purely to read
// `exp` on the client so we can pre-empt sending requests with an obviously
// dead token.
export function decodeJwtPayload(token: string): { exp?: number; iat?: number; userId?: string } | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function isJwtExpired(token: string, skewSeconds = 5): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return false;
  const nowSec = Math.floor(Date.now() / 1000);
  return payload.exp <= nowSec - skewSeconds;
}
